import { JSONContent } from '@tiptap/core';
import { Node as PMNode, Schema } from '@tiptap/pm/model';

/*
 * Converts between the stored prompt (Markdown text, sent to the model
 * verbatim) and the editor document (real headings, lists and marks, so admins
 * see formatting instead of "#" or "**").
 *
 * Only the formats the toolbar offers are recognised, and nothing is escaped.
 * Parsing is verified by serializing the result again: when that does not give
 * back exactly the stored text, the affected lines are loaded as plain text.
 * Opening and saving a prompt therefore never changes it.
 */

type MarkName = 'bold' | 'italic' | 'underline' | 'code';
type InlineMark = { type: MarkName; attrs?: Record<string, unknown> };
type InlineNode = { type: 'text'; text: string; marks?: InlineMark[] };

// Outer to inner, so **_<u>text</u>_** nests the same way every time
const MARK_ORDER: MarkName[] = ['bold', 'italic', 'underline', 'code'];

// Group 1 is the character before the marker (or "" at the start), group 2
// the formatted text. Lookbehind is deliberately not used: Safari < 16.4 can't
// parse it, and one such regex would stop the whole app bundle from loading.
// Inner text must start and end with a non-space character; the optional
// tail is lazy ("??") so the shortest match wins, e.g. "*a* *b*" is two
// italics, not one.
const INLINE_PATTERNS: Array<{
  re: RegExp;
  mark: MarkName;
  marker?: string;
}> = [
  { re: /()`([^`]+)`/, mark: 'code' },
  { re: /()\*\*(\S(?:.*?\S)??)\*\*/, mark: 'bold' },
  { re: /()<u>(.+?)<\/u>/, mark: 'underline' },
  {
    re: /(^|[^*])\*([^\s*](?:.*?[^\s*])??)\*(?!\*)/,
    mark: 'italic',
    marker: '*',
  },
  {
    // Word-bounded so snake_case names such as {{user.first_name}} stay text
    re: /(^|[^_\w])_([^\s_](?:.*?[^\s_])??)_(?![_\w])/,
    mark: 'italic',
    marker: '_',
  },
];

// The spacing after a marker is captured separately (see prompt-blocks.ts)
const HEADING_LINE = /^(#{1,3})([ \t]+)(.*)$/;
const QUOTE_LINE = /^>([ \t]+)(.*)$/;
const BULLET_LINE = /^((?: {2})*)-([ \t]+)(.*)$/;
const ORDERED_LINE = /^((?: {2})*)(\d{1,9})\.([ \t]+)(.*)$/;

// ---------------------------------------------------------------------------
// Inline: text <-> text nodes with marks

const textNode = (text: string, marks: InlineMark[]): InlineNode =>
  marks.length ? { type: 'text', text, marks } : { type: 'text', text };

const parseInlineRaw = (text: string, marks: InlineMark[]): InlineNode[] => {
  if (!text) return [];

  let best: {
    pattern: (typeof INLINE_PATTERNS)[number];
    start: number;
    full: string;
    content: string;
  } | null = null;
  for (const pattern of INLINE_PATTERNS) {
    const match = pattern.re.exec(text);
    if (!match) continue;
    // Skip the preceding character captured in group 1
    const start = match.index + match[1].length;
    if (!best || start < best.start) {
      best = {
        pattern,
        start,
        full: match[0].slice(match[1].length),
        content: match[2],
      };
    }
  }
  if (!best) return [textNode(text, marks)];

  const { pattern, start, full, content } = best;
  const before = text.slice(0, start);
  const after = text.slice(start + full.length);
  // A mark can't be nested in itself, and code can't hold other marks
  const nested =
    marks.some((mark) => mark.type === pattern.mark) ||
    (pattern.mark === 'code' && marks.length > 0);

  const inner: InlineNode[] = nested
    ? [textNode(full, marks)]
    : pattern.mark === 'code'
      ? [textNode(content, [{ type: 'code' }])]
      : parseInlineRaw(content, [
          ...marks,
          pattern.marker
            ? { type: pattern.mark, attrs: { marker: pattern.marker } }
            : { type: pattern.mark },
        ]);

  return [
    ...parseInlineRaw(before, marks),
    ...inner,
    ...parseInlineRaw(after, marks),
  ];
};

const sameMarks = (a?: InlineMark[], b?: InlineMark[]) =>
  JSON.stringify(a ?? []) === JSON.stringify(b ?? []);

const parseInline = (text: string): InlineNode[] => {
  const merged: InlineNode[] = [];
  for (const node of parseInlineRaw(text, [])) {
    const last = merged[merged.length - 1];
    if (last && sameMarks(last.marks, node.marks)) last.text += node.text;
    else merged.push({ ...node });
  }
  // Only keep the formatting if it reproduces the text exactly
  if (serializeInline(merged) === text) return merged;
  return text ? [textNode(text, [])] : [];
};

const markKey = (mark: InlineMark) =>
  `${mark.type}:${JSON.stringify(mark.attrs?.marker ?? null)}`;

const serializeInline = (nodes: InlineNode[]): string => {
  let out = '';
  // Whitespace at the edge of a mark is written outside the markers
  let pending = '';
  const stack: Array<{ key: string; close: string }> = [];

  const closeTo = (length: number) => {
    while (stack.length > length) out += stack.pop()!.close;
  };

  for (const node of nodes) {
    const marks = [...(node.marks ?? [])].sort(
      (a, b) => MARK_ORDER.indexOf(a.type) - MARK_ORDER.indexOf(b.type)
    );
    const keys = marks.map(markKey);
    let keep = 0;
    while (keep < stack.length && keys.includes(stack[keep].key)) keep += 1;

    if (!node.text.trim()) {
      // Whitespace never opens a mark, but it ends the ones it doesn't have:
      // "*a* *b*" is two italics, with the space between them outside both
      closeTo(keep);
      pending += node.text;
      continue;
    }
    const lead = node.text.match(/^\s*/)?.[0] ?? '';
    const trail = node.text.slice(lead.length).match(/\s*$/)?.[0] ?? '';
    const core = node.text.slice(lead.length, node.text.length - trail.length);

    closeTo(keep);
    out += pending + lead;
    pending = '';

    const hasBold = marks.some((mark) => mark.type === 'bold');
    for (const mark of marks) {
      const key = markKey(mark);
      if (stack.some((open) => open.key === key)) continue;
      let open: string;
      let close: string;
      switch (mark.type) {
        case 'bold':
          open = close = '**';
          break;
        case 'underline':
          open = '<u>';
          close = '</u>';
          break;
        case 'code':
          open = close = '`';
          break;
        default:
          // "_" inside bold so bold italic is **_text_**, not ***text***
          open = close =
            (mark.attrs?.marker as string | undefined) ?? (hasBold ? '_' : '*');
      }
      out += open;
      stack.push({ key, close });
    }
    out += core;
    pending = trail;
  }

  closeTo(0);
  return out + pending;
};

const inlineOf = (node: PMNode): InlineNode[] => {
  const nodes: InlineNode[] = [];
  node.forEach((child) => {
    if (!child.isText) return;
    nodes.push({
      type: 'text',
      text: child.text ?? '',
      marks: child.marks.map((mark) => ({
        type: mark.type.name as MarkName,
        attrs:
          mark.type.name === 'italic'
            ? { marker: mark.attrs.marker }
            : undefined,
      })),
    });
  });
  return nodes;
};

// ---------------------------------------------------------------------------
// Blocks: lines <-> document

const paragraph = (text: string): JSONContent => {
  const content = parseInline(text);
  return content.length
    ? { type: 'paragraph', content }
    : { type: 'paragraph' };
};

const literalParagraph = (text: string): JSONContent =>
  text
    ? { type: 'paragraph', content: [{ type: 'text', text }] }
    : { type: 'paragraph' };

// ---------------------------------------------------------------------------
// Tables: "| a | b |" rows with a "| --- | --- |" separator after the header

const SEPARATOR_CELL = /^\s*:?-+:?\s*$/;
const CODE_FENCE = /^[ \t]*(```|~~~)/;
const ESCAPED_PIPE = /\\\|/g;

/** The cells of a "| a | b |" row (text between unescaped pipes), or null. */
export const splitTableRow = (line: string): string[] | null => {
  if (line.length < 2 || !line.startsWith('|') || !line.endsWith('|')) {
    return null;
  }
  const inner = line.slice(1, -1);
  // The closing "|" is escaped, so it isn't the end of the row
  if (inner.endsWith('\\')) return null;

  const cells: string[] = [];
  let current = '';
  for (let index = 0; index < inner.length; index += 1) {
    const character = inner[index];
    if (character === '\\' && inner[index + 1] === '|') {
      current += '\\|';
      index += 1;
    } else if (character === '|') {
      cells.push(current);
      current = '';
    } else {
      current += character;
    }
  }
  cells.push(current);
  return cells;
};

const tableCell = (raw: string, type: string): JSONContent => {
  const padBefore = raw.match(/^\s*/)?.[0] ?? '';
  const rest = raw.slice(padBefore.length);
  const padAfter = rest.match(/\s*$/)?.[0] ?? '';
  const text = rest
    .slice(0, rest.length - padAfter.length)
    .replace(ESCAPED_PIPE, '|');
  return { type, attrs: { padBefore, padAfter }, content: [paragraph(text)] };
};

/**
 * The table starting at `start`, if any, and the index after its last row.
 * It is only used if it saves back exactly as stored; otherwise its lines are
 * loaded like any other text.
 */
const parseTable = (
  lines: string[],
  start: number,
  schema: Schema
): { json: JSONContent; end: number } | null => {
  const header = splitTableRow(lines[start]);
  const separator =
    start + 1 < lines.length ? splitTableRow(lines[start + 1]) : null;
  if (
    !header ||
    !separator ||
    separator.length !== header.length ||
    !separator.every((cell) => SEPARATOR_CELL.test(cell))
  ) {
    return null;
  }

  const rows = [header];
  let end = start + 2;
  while (end < lines.length) {
    const cells = splitTableRow(lines[end]);
    if (!cells || cells.length !== header.length) break;
    rows.push(cells);
    end += 1;
  }

  const json: JSONContent = {
    type: 'table',
    attrs: { separator: lines[start + 1] },
    content: rows.map((cells, rowIndex) => ({
      type: 'tableRow',
      content: cells.map((cell) =>
        tableCell(cell, rowIndex === 0 ? 'tableHeader' : 'tableCell')
      ),
    })),
  };

  try {
    const saved: string[] = [];
    serializeBlock(schema.nodeFromJSON(json), saved, '', 0);
    if (saved.join('\n') === lines.slice(start, end).join('\n')) {
      return { json, end };
    }
  } catch {
    // Not a valid table for the editor: keep the lines as text
  }
  return null;
};

/** The stored separator row, or one rebuilt after columns were added/removed. */
const tableSeparator = (stored: string | null, columns: number) => {
  const cells = stored ? splitTableRow(stored) : null;
  if (stored && cells?.length === columns) return stored;
  const kept = cells ?? [];
  return `|${Array.from({ length: columns }, (_, index) => kept[index] ?? ' --- ').join('|')}|`;
};

const serializeTable = (node: PMNode, lines: string[], quote: string) => {
  const rows: string[][] = [];
  node.forEach((row) => {
    const cells: string[] = [];
    row.forEach((cell) => {
      const content = cell.firstChild
        ? serializeInline(inlineOf(cell.firstChild)).replace(/\|/g, '\\|')
        : '';
      cells.push(
        `${cell.attrs.padBefore ?? ' '}${content}${cell.attrs.padAfter ?? ' '}`
      );
    });
    rows.push(cells);
  });
  if (!rows.length) return;

  const columns = Math.max(...rows.map((cells) => cells.length));
  const row = (cells: string[]) => `${quote}|${cells.join('|')}|`;
  lines.push(row(rows[0]));
  lines.push(quote + tableSeparator(node.attrs.separator, columns));
  rows.slice(1).forEach((cells) => lines.push(row(cells)));
};

const buildDocument = (
  lines: string[],
  merge: boolean,
  schema: Schema
): JSONContent => {
  const blocks: JSONContent[] = [];
  let lists: Array<{ list: JSONContent; ordered: boolean; next: number }> = [];
  let quote: JSONContent | null = null;
  let inCodeBlock = false;

  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index];

    // Code blocks aren't an editor format: their lines, fences included, stay
    // exactly as written, with nothing inside them read as formatting
    if (CODE_FENCE.test(line) || inCodeBlock) {
      if (CODE_FENCE.test(line)) inCodeBlock = !inCodeBlock;
      lists = [];
      quote = null;
      blocks.push(literalParagraph(line));
      continue;
    }

    const table = parseTable(lines, index, schema);
    if (table) {
      lists = [];
      quote = null;
      blocks.push(table.json);
      index = table.end - 1;
      continue;
    }

    const bullet = line.match(BULLET_LINE);
    const numbered = bullet ? null : line.match(ORDERED_LINE);
    const item = bullet ?? numbered;

    if (item) {
      quote = null;
      const depth = item[1].length / 2;
      const ordered = Boolean(numbered);
      const number = numbered ? Number(numbered[2]) : 1;
      const markerGap = numbered ? numbered[3] : item[2];
      const text = numbered ? numbered[4] : item[3];
      const listItem = (): JSONContent => ({
        type: 'listItem',
        attrs: { markerGap },
        content: [paragraph(text)],
      });

      if (!merge) {
        // Fallback: one list per line, top level only
        if (depth === 0) {
          blocks.push({
            type: ordered ? 'orderedList' : 'bulletList',
            ...(ordered ? { attrs: { start: number } } : {}),
            content: [listItem()],
          });
        } else {
          blocks.push(literalParagraph(line));
        }
        continue;
      }

      lists = lists.slice(0, depth + 1);
      let current = lists[depth];
      const continues =
        current &&
        current.ordered === ordered &&
        (!ordered || number === current.next);

      if (!continues) {
        const parent = lists[depth - 1];
        if (depth > 0 && !parent) {
          // Indented item without a parent item: keep the line as text
          lists = [];
          blocks.push(literalParagraph(line));
          continue;
        }
        const list: JSONContent = {
          type: ordered ? 'orderedList' : 'bulletList',
          ...(ordered ? { attrs: { start: number } } : {}),
          content: [],
        };
        if (depth === 0) blocks.push(list);
        else {
          const items = parent.list.content!;
          items[items.length - 1].content!.push(list);
        }
        current = { list, ordered, next: number };
        lists[depth] = current;
      }

      current.list.content!.push(listItem());
      current.next = number + 1;
      continue;
    }

    lists = [];
    const quoted = line.match(QUOTE_LINE);
    if (quoted) {
      const quoteLine: JSONContent = {
        ...paragraph(quoted[2]),
        attrs: { quoteGap: quoted[1] },
      };
      if (merge && quote) quote.content!.push(quoteLine);
      else {
        quote = { type: 'blockquote', content: [quoteLine] };
        blocks.push(quote);
      }
      continue;
    }
    quote = null;

    const heading = line.match(HEADING_LINE);
    if (heading) {
      const content = parseInline(heading[3]);
      blocks.push({
        type: 'heading',
        attrs: { level: heading[1].length, markerGap: heading[2] },
        ...(content.length ? { content } : {}),
      });
    } else blocks.push(paragraph(line));
  }

  markJoinedBlocks(blocks);
  return {
    type: 'doc',
    content: blocks.length ? blocks : [{ type: 'paragraph' }],
  };
};

const serializeBlock = (
  node: PMNode,
  lines: string[],
  quote: string,
  depth: number
) => {
  switch (node.type.name) {
    case 'paragraph':
      lines.push(quote + serializeInline(inlineOf(node)));
      break;
    case 'heading':
      lines.push(
        `${quote}${'#'.repeat(node.attrs.level)}${node.attrs.markerGap ?? ' '}${serializeInline(inlineOf(node))}`
      );
      break;
    case 'blockquote':
      node.forEach((child) => {
        if (child.type.name === 'paragraph') {
          lines.push(
            `${quote}>${child.attrs.quoteGap ?? ' '}${serializeInline(inlineOf(child))}`
          );
        } else {
          serializeBlock(child, lines, `${quote}> `, depth);
        }
      });
      break;
    case 'bulletList':
    case 'orderedList': {
      const ordered = node.type.name === 'orderedList';
      const start = Number(node.attrs.start ?? 1);
      const indent = '  '.repeat(depth);
      node.forEach((item, _offset, index) => {
        const gap = item.attrs.markerGap ?? ' ';
        const marker = ordered ? `${start + index}.${gap}` : `-${gap}`;
        let wroteMarker = false;
        item.forEach((child) => {
          if (
            child.type.name === 'bulletList' ||
            child.type.name === 'orderedList'
          ) {
            if (!wroteMarker) lines.push(quote + indent + marker.trimEnd());
            wroteMarker = true;
            serializeBlock(child, lines, quote, depth + 1);
          } else if (!wroteMarker && child.type.name === 'paragraph') {
            lines.push(
              quote + indent + marker + serializeInline(inlineOf(child))
            );
            wroteMarker = true;
          } else {
            if (!wroteMarker) lines.push(quote + indent + marker.trimEnd());
            wroteMarker = true;
            serializeBlock(child, lines, `${quote}${indent}  `, 0);
          }
        });
        if (!wroteMarker) lines.push(quote + indent + marker);
      });
      break;
    }
    case 'table':
      serializeTable(node, lines, quote);
      break;
    default:
      lines.push(quote + node.textContent);
  }
};

/** The stored prompt text for an editor document (or a pasted/copied slice). */
// ---------------------------------------------------------------------------
// Blank lines between top-level blocks (see blockSpacing in prompt-blocks.ts)

const SEPARATED_BLOCKS = new Set([
  'paragraph',
  'heading',
  'bulletList',
  'orderedList',
  'blockquote',
  'table',
]);

/**
 * Whether standard Markdown needs a blank line between these two blocks to
 * read them as separate blocks: blocks of different types, and two tables or
 * two quotes in a row (they'd merge). Two paragraphs in a row are left as they
 * are: Markdown reads them as one paragraph with a line break, same meaning.
 */
const needsBlankLine = (above: string, below: string) =>
  SEPARATED_BLOCKS.has(above) &&
  SEPARATED_BLOCKS.has(below) &&
  (above !== below || above === 'table' || above === 'blockquote');

const isBlankLine = (type: string, isEmpty: boolean) =>
  type === 'paragraph' && isEmpty;

// Unique for the whole session, so ids from separate loads or pastes never
// collide
let nextBlockId = 1;

/**
 * Give loaded blocks an id, and record for each block stored with no blank
 * line above it the id of the block it was stored under.
 */
const markJoinedBlocks = (blocks: JSONContent[]) => {
  blocks.forEach((block) => {
    block.attrs = { ...block.attrs, blockId: `b${nextBlockId}` };
    nextBlockId += 1;
  });
  blocks.forEach((block, index) => {
    const above = blocks[index - 1];
    if (
      above &&
      !isBlankLine(above.type!, !above.content?.length) &&
      !isBlankLine(block.type!, !block.content?.length) &&
      needsBlankLine(above.type!, block.type!)
    ) {
      block.attrs = { ...block.attrs, joinedTo: above.attrs!.blockId };
    }
  });
};

export const serializeMarkdown = (doc: PMNode): string => {
  const lines: string[] = [];
  let above: PMNode | null = null;
  doc.forEach((node) => {
    // Kept tight only while it's still under the block it was stored under
    const stillJoined =
      node.attrs.joinedTo != null &&
      above?.attrs.blockId === node.attrs.joinedTo;
    if (
      above &&
      !stillJoined &&
      !isBlankLine(above.type.name, above.content.size === 0) &&
      !isBlankLine(node.type.name, node.content.size === 0) &&
      needsBlankLine(above.type.name, node.type.name)
    ) {
      lines.push('');
    }
    serializeBlock(node, lines, '', 0);
    above = node;
  });
  return lines.join('\n');
};

/** Editor content for a stored prompt; guaranteed to serialize back to it. */
export const parseMarkdown = (text: string, schema: Schema): JSONContent => {
  const normalized = (text ?? '').replace(/\r\n?/g, '\n');
  const lines = normalized.split('\n');

  for (const merge of [true, false]) {
    const json = buildDocument(lines, merge, schema);
    try {
      if (serializeMarkdown(schema.nodeFromJSON(json)) === normalized)
        return json;
    } catch {
      // Invalid structure: try the next, simpler interpretation
    }
  }

  return { type: 'doc', content: lines.map(literalParagraph) };
};
